import * as React from "react";
import { useState } from "react";
import {
	Avatar,
	Box,
	Button,
	Collapse,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import Logout from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import isAuthenticated from "../../Utils/isAuthenticated";
import InstructorSearchAutocomplete from "../../Utils/InstructorSearchAutocomplete";

export default function ProfileMenu({ Logout: onLogout }) {
	const auth = isAuthenticated();
	const access = auth?.access ?? null;

	// Persist the real access so admin tools survive access changes
	const originalAccess = sessionStorage.getItem("original_access");
	const isRealAdmin = (originalAccess ?? access)?.includes("ADMIN") ?? false;
	const isOverridden = originalAccess !== null && originalAccess !== access;

	const ccid = auth?.ccid ?? null;
	const empid = auth?.empid ?? null;

	// Persist the real identity so we can restore after impersonation
	const originalCcid = sessionStorage.getItem("original_ccid");
	const originalEmpid = sessionStorage.getItem("original_empid");
	const isImpersonating =
		(originalCcid !== null && originalCcid !== ccid) ||
		(originalEmpid !== null && originalEmpid !== empid);

	const firstName = sessionStorage.getItem("first_name");
	const lastName = sessionStorage.getItem("last_name");
	const fullName = [firstName, lastName].filter(Boolean).join(" ") || ccid;
	const avatarLetter = firstName?.charAt(0).toUpperCase() || "?";

	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const [impersonateOpen, setImpersonateOpen] = useState(false);
	const [empidValue, setEmpidValue] = useState("");
	const [ccidValue, setCcidValue] = useState("");

	const [accessChangeOpen, setAccessChangeOpen] = useState(false);

	const handleClick = (event) => setAnchorEl(event.currentTarget);
	const handleClose = () => {
		setAnchorEl(null);
		setImpersonateOpen(false);
	};

	const handleLogout = () => {
		onLogout();
		setAnchorEl(null);
	};

	const handleToggleImpersonate = (e) => {
		e.stopPropagation();
		setImpersonateOpen((prev) => !prev);
	};

	const handleSetEmpid = () => {
		if (!sessionStorage.getItem("original_empid")) {
			sessionStorage.setItem("original_empid", empid ?? "");
		}
		if (!sessionStorage.getItem("original_ccid")) {
			sessionStorage.setItem("original_ccid", ccid ?? "");
		}
		sessionStorage.setItem("id", empidValue);
		window.location.reload();
	};

	const handleSetCcid = () => {
		if (!sessionStorage.getItem("original_ccid")) {
			sessionStorage.setItem("original_ccid", ccid ?? "");
		}
		if (!sessionStorage.getItem("original_empid")) {
			sessionStorage.setItem("original_empid", empid ?? "");
		}
		sessionStorage.setItem("ccid", ccidValue);
		window.location.reload();
	};

	const handleToggleAccessChange = (e) => {
		e.stopPropagation();
		setAccessChangeOpen((prev) => !prev);
	};

	const handleAccessChange = (e) => {
		// Save the real access the first time so we can always restore it
		if (!sessionStorage.getItem("original_access")) {
			sessionStorage.setItem("original_access", access ?? "");
		}

		if (e.target.name === "NONE") {
			sessionStorage.setItem("access", "");
		} else {
			sessionStorage.setItem("access", e.target.name);
		}
		window.location.reload();
	};

	const handleRestoreAccess = () => {
		const original = sessionStorage.getItem("original_access");
		if (original !== null) {
			sessionStorage.setItem("access", original);
			sessionStorage.removeItem("original_access");
		}
		window.location.reload();
	};

	const handleRestoreIdentity = () => {
		const origCcid = sessionStorage.getItem("original_ccid");
		const origEmpid = sessionStorage.getItem("original_empid");
		if (origCcid !== null) {
			sessionStorage.setItem("ccid", origCcid);
			sessionStorage.removeItem("original_ccid");
		}
		if (origEmpid !== null) {
			sessionStorage.setItem("id", origEmpid);
			sessionStorage.removeItem("original_empid");
		}
		window.location.reload();
	};

	return (
		<>
			<div className="flex items-center relative">
				<Tooltip title="Account">
					<IconButton
						onClick={handleClick}
						size="small"
						aria-controls={open ? "account-menu" : undefined}
						aria-haspopup="true"
						aria-expanded={open ? "true" : undefined}
					>
						<Avatar
							sx={{
								width: 36,
								height: 36,
								color: "primary.main",
								fontSize: "1rem",
								fontWeight: 600,
								border: "1px solid #F8f8f8",
								backgroundColor: "#f5f5f5",
							}}
						>
							{avatarLetter}
						</Avatar>
					</IconButton>
				</Tooltip>

				<Menu
					anchorEl={anchorEl}
					id="account-menu"
					open={open}
					onClose={handleClose}
					MenuListProps={{ disableListWrap: true }}
					slotProps={{
						paper: {
							elevation: 4,
							sx: {
								minWidth: 400,
								mt: 1,
								borderRadius: 2,
								// border: 2,
								// borderColor: "primary.main",
								overflow: "visible",
								"&:before": {
									content: '""',
									display: "block",
									position: "absolute",
									top: 0,
									right: 14,
									width: 10,
									height: 10,
									bgcolor: "background.paper",
									transform: "translateY(-50%) rotate(45deg)",
									zIndex: 0,
								},
							},
						},
					}}
					transformOrigin={{ horizontal: "right", vertical: "top" }}
					anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				>
					{/* User Info Header */}
					<Box sx={{ px: 2, py: 1.5 }}>
						<Typography variant="subtitle1" fontWeight={600}>
							{fullName}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{ccid} - {empid}
						</Typography>
					</Box>

					{isOverridden && (
						<Box
							sx={{
								mx: 2,
								mb: 1,
								px: 1.5,
								py: 1,
								borderRadius: 1.5,
								background: "#fef3c7",
								border: "1px solid #fcd34d",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
							}}
						>
							<Typography variant="caption" sx={{ color: "#92400e", fontWeight: 600 }}>
								Access: {access || "NONE"}
							</Typography>
							<Button
								size="small"
								onClick={handleRestoreAccess}
								sx={{
									minWidth: 0,
									px: 1.5,
									py: 0.25,
									fontSize: "0.7rem",
									color: "#92400e",
									borderColor: "#d97706",
									"&:hover": { background: "#fde68a" },
								}}
								variant="outlined"
							>
								Restore
							</Button>
						</Box>
					)}

					{isImpersonating && (
						<Box
							sx={{
								mx: 2,
								mb: 1,
								px: 1.5,
								py: 1,
								borderRadius: 1.5,
								background: "#ede9fe",
								border: "1px solid #c4b5fd",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
							}}
						>
							<Typography variant="caption" sx={{ color: "#5b21b6", fontWeight: 600 }}>
								Impersonating: {ccid}
							</Typography>
							<Button
								size="small"
								onClick={handleRestoreIdentity}
								sx={{
									minWidth: 0,
									px: 1.5,
									py: 0.25,
									fontSize: "0.7rem",
									color: "#5b21b6",
									borderColor: "#7c3aed",
									"&:hover": { background: "#ddd6fe" },
								}}
								variant="outlined"
							>
								Restore
							</Button>
						</Box>
					)}

					<Divider />

					{/* Admin Impersonate Section */}
					{isRealAdmin && (
						<>
							<MenuItem onClick={handleToggleImpersonate} sx={{ py: 1.5, mt: 1 }}>
								<ListItemIcon>
									<PersonIcon fontSize="small" color="white" />
								</ListItemIcon>
								<ListItemText primary="Impersonate" />
								{impersonateOpen ? (
									<ExpandLessIcon fontSize="small" />
								) : (
									<ExpandMoreIcon fontSize="small" />
								)}
							</MenuItem>

							<Collapse in={impersonateOpen} timeout="auto" unmountOnExit>
								<Box
									sx={{
										px: 2,
										py: 1.5,
										m: 1,
										bgcolor: "grey.50",
										display: "flex",
										flexDirection: "column",
										gap: 1.5,
									}}
									onClick={(e) => e.stopPropagation()}
									onKeyDown={(e) => e.stopPropagation()}
								>
									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										Search by name
									</Typography>
									<InstructorSearchAutocomplete />

									<Divider sx={{ my: 0.5 }} />

									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										Or enter manually
									</Typography>

									<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
										<TextField
											label="EmpId"
											variant="outlined"
											size="small"
											value={empidValue}
											onChange={(e) => setEmpidValue(e.target.value)}
											sx={{ flex: 1 }}
										/>
										<Button
											variant="contained"
											size="small"
											onClick={handleSetEmpid}
											disabled={!empidValue}
										>
											Set
										</Button>
									</Box>

									<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
										<TextField
											label="CCID"
											variant="outlined"
											size="small"
											value={ccidValue}
											onChange={(e) => setCcidValue(e.target.value)}
											sx={{ flex: 1 }}
										/>
										<Button
											variant="contained"
											size="small"
											onClick={handleSetCcid}
											disabled={!ccidValue}
										>
											Set
										</Button>
									</Box>
								</Box>
							</Collapse>
							<MenuItem onClick={handleToggleAccessChange} sx={{ py: 1.5, mt: 1 }}>
								<ListItemIcon>
									<PersonIcon fontSize="small" color="white" />
								</ListItemIcon>
								<ListItemText primary="Change Access" />
								{accessChangeOpen ? (
									<ExpandLessIcon fontSize="small" />
								) : (
									<ExpandMoreIcon fontSize="small" />
								)}
							</MenuItem>
							<Collapse in={accessChangeOpen} timeout="auto" unmountOnExit>
								<Box
									sx={{
										px: 2,
										py: 1.5,
										m: 1,
										bgcolor: "grey.50",
										display: "flex",
										flexDirection: "column",
										gap: 1.5,
									}}
									onClick={(e) => e.stopPropagation()}
									onKeyDown={(e) => e.stopPropagation()}
								>
									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										Select Access Level
									</Typography>

									<Divider sx={{ my: 0.5 }} />

									<Box>
										<Button onClick={handleAccessChange} name="INST">
											INST
										</Button>
										<Button onClick={handleAccessChange} name="STD">
											STD
										</Button>
										<Button onClick={handleAccessChange} name="DDC">
											DDC
										</Button>
										<Button onClick={handleAccessChange} name="WEB">
											WEB
										</Button>
										<Button onClick={handleAccessChange} name="TEMP">
											TEMP
										</Button>
										<Button onClick={handleAccessChange} name="ADMIN">
											ADMIN
										</Button>
										<Button onClick={handleAccessChange} name="NONE">
											NONE
										</Button>
									</Box>
								</Box>
							</Collapse>
							<Divider />
						</>
					)}

					{/* Logout */}
					<MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
						<ListItemIcon>
							<Logout fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Logout" />
					</MenuItem>
				</Menu>
			</div>
		</>
	);
}
